import { useRef } from "react";
import { useNavigate } from "react-router-dom";
import { MessageCircleQuestion, Sparkles, FileSearch, FileBarChart, ArrowRight } from "lucide-react";
import { Reveal, Tag, H2, GoldBtn, GhostBtn } from "@/components/sections/shared";
import { ParticleCard, GlobalSpotlight, useMobileDetection } from "@/components/MagicBento/MagicBento";
import FoldText from "@/components/FoldText/FoldText";

const comingSoon = [
  { icon: FileSearch, title: "Advanced Claim Review", copy: "For more complex claim situations that may require deeper documentation review or escalation guidance." },
  { icon: FileBarChart, title: "Premium Reports", copy: "Detailed reports that help organize your claim facts, possible red flags, and recommended next steps." },
];

export default function Offers() {
  const gridRef = useRef(null);
  const isMobile = useMobileDetection();
  const navigate = useNavigate();

  return (
    <section id="offers" data-testid="offers-section" className="relative bg-[#0A0A0C] py-24 md:py-32">
      <GlobalSpotlight
        gridRef={gridRef}
        disableAnimations={isMobile}
        glowColor="138, 43, 226"
        spotlightRadius={280}
      />
      <div className="max-w-[1280px] mx-auto px-6 lg:px-14">
        <Reveal>
          <Tag testId="offers-tag">Offers</Tag>
          <H2>
            <FoldText
              text="Start free. Upgrade when you need more help."
              splitBy="word"
              trigger="scroll"
              fontSize="inherit"
              fontWeight="inherit"
              color="inherit"
            />
          </H2>
          <p className="mt-6 max-w-2xl text-[#A1A1AA] font-light leading-relaxed">
            Inssux is designed to help you get clarity first. Start with Jack. Run the Rip-Off
            Detector. Understand what may be going on. If your situation needs deeper assistance,
            Jack can guide you toward premium options.
          </p>
        </Reveal>

        <div ref={gridRef} className="bento-section">
          <div className="mt-14 grid lg:grid-cols-2 gap-5">
            <Reveal>
              <ParticleCard
                testId="offer-free"
                disableAnimations={isMobile}
                glowColor="255, 209, 102"
                className="magic-bento-card magic-bento-card--border-glow h-full bg-[#1A1A1D] border-2 border-[#FFD166]/60 p-9"
                overflowContent={
                  <div className="absolute -top-3 left-8 z-10 bg-[#FFD166] px-3 py-1 font-mono-file text-[9px] tracking-[0.25em] text-black">
                    START HERE · FREE
                  </div>
                }
              >
                <MessageCircleQuestion size={24} className="text-[#FFD166]" />
                <h3 className="mt-4 font-display text-xl tracking-wide text-white">Free Ask Jack Question</h3>
                <p className="mt-3 text-[#A1A1AA] font-light leading-relaxed">
                  Start with one question and receive claim guidance in plain English.
                </p>
              </ParticleCard>
            </Reveal>
            <Reveal delay={0.1}>
              <ParticleCard
                testId="offer-premium"
                disableAnimations={isMobile}
                glowColor="138, 43, 226"
                className="magic-bento-card magic-bento-card--border-glow h-full bg-[#1A1A1D] border border-[#8A2BE2]/60 p-9"
              >
                <Sparkles size={24} className="text-[#9D4EDD]" />
                <h3 className="mt-4 font-display text-xl tracking-wide text-white">Ask Jack Premium</h3>
                <p className="mt-3 text-[#A1A1AA] font-light leading-relaxed">
                  Continue the conversation with Jack for deeper claim guidance and next-step support.
                  Pricing to be finalized.
                </p>
              </ParticleCard>
            </Reveal>
          </div>

          <div className="mt-5 grid md:grid-cols-2 gap-5">
            {comingSoon.map((o, i) => (
              <Reveal key={o.title} delay={i * 0.1}>
                <ParticleCard
                  testId={`offer-coming-${i}`}
                  disableAnimations={isMobile}
                  glowColor="138, 43, 226"
                  className="magic-bento-card magic-bento-card--border-glow relative h-full bg-[#121214] border border-white/10 p-7"
                >
                  <span className="absolute top-4 right-4 font-mono-file text-[9px] tracking-[0.2em] text-[#9D4EDD] border border-[#8A2BE2]/40 px-2 py-0.5">
                    COMING SOON
                  </span>
                  <o.icon size={20} className="text-[#71717A]" />
                  <h3 className="mt-3 font-display text-base tracking-wide text-white/90">{o.title}</h3>
                  <p className="mt-2 text-sm text-[#71717A] font-light leading-relaxed">{o.copy}</p>
                </ParticleCard>
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal delay={0.3}>
          <div className="mt-11 flex flex-wrap items-center gap-4">
            <GoldBtn testId="offers-primary-cta" onClick={() => window.AIWidget?.ask()}>
              START WITH JACK
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </GoldBtn>
            <GhostBtn testId="offers-secondary-cta" onClick={() => navigate("/rip-off-detector")}>
              TAKE THE FREE RIP-OFF DETECTOR
            </GhostBtn>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
